import { useEffect, useRef } from 'react';

// Attach the returned ref to a sentinel element at the end of a scrollable list; `onLoadMore`
// fires whenever that sentinel scrolls into view (with a 200px pre-fetch margin so the next page
// is already loading before the user hits the bottom). Gated by `enabled` — pass
// `hasNextPage && !isFetchingNextPage` so we neither over-fetch nor fire past the end of the list.
export function useInfiniteScroll(onLoadMore: () => void, enabled: boolean) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    // Nothing to observe, or paging is exhausted / in flight — don't wire an observer up.
    if (!el || !enabled) return;
    // jsdom (tests) has no IntersectionObserver; bail rather than throw. The page still renders,
    // it just won't auto-page in that environment.
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onLoadMore, enabled]);

  return sentinelRef;
}
